import { type JSX, useEffect, useState } from "react";

import EmptyPlaceholder from "common/components/EmptyPlaceholder";
import ErrorPlaceholder from "common/components/ErrorPlaceholder";
import PaginationFooter from "common/components/PaginationFooter";
import SearchInput from "common/components/SearchInput";
import { Button } from "common/components/ui/Button";
import WorkspaceAccountSelector from "common/components/WorkspaceAccountSelector";
import { getWhitelabelCustomization } from "common/helpers/utils";
import useDebounce from "common/hooks/useDebounce";
import usePagination from "common/hooks/usePagination";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import useNavigatorLeads from "../../../datahooks/useNavigatorLeads";
import useSelectedMinerAccount from "../../../hooks/useSelectedMinerAccount";
import ScrollAndFooter from "../ScrollAndFooter";
import SearchAndFiltersEmptyPlaceholder from "../SearchAndFiltersEmptyPlaceholder";
import DesktopFilters from "./Filters/DesktopFilters";
import MobileFilters from "./Filters/MobileFilters";
import NavigatorMobileList from "./NavigatorMobileList";
import NavigatorTable from "./NavigatorTable";
import UrlTargeting from "./UrlTargeting";

import type {
  NavigatorLeadListSource,
  SelectedNavigatorFilters,
} from "../../../types";

import ErrorPlaceholderImage from "assets/images/empty-placeholders/error.png";

interface NavigatorProps {
  createSource: (params: {
    miner: string;
    settings: NavigatorLeadListSource["settings"];
  }) => void;
  isCreatingSource: boolean;
}

export default function Navigator({
  createSource,
  isCreatingSource,
}: NavigatorProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { setMinerAccountId, minerAccountId, minerAccount } =
    useSelectedMinerAccount(true);

  const { page, setPage, pageSize, setPageSize } = usePagination(
    "navigator-source-leads",
  );

  const [inputValue, setInputValue] = useState("");
  const [keywords, setKeywords] = useDebounce(inputValue);
  const [selectedFilters, setSelectedFilters] =
    useState<SelectedNavigatorFilters>([]);

  const {
    leads,
    filters,
    totalCount,
    isFetchingSalesNavigatorLeads,
    refetchNavigatorLeads,
    navigatorLeadsError,
    isLimitExceeded,
  } = useNavigatorLeads(
    { accountId: minerAccountId, selectedFilters, keywords },
    { page, pageSize },
  );

  // any change to the search criteria invalidates the current page
  useEffect(() => {
    setPage(0);
  }, [selectedFilters, keywords]);

  const isSearchAccountDisconnected = minerAccount?.state !== "LoggedIn";
  const isSearchAccountNavigatorDisconnected = !minerAccount?.sales_navigator;

  let content: JSX.Element;

  if (isSearchAccountDisconnected) {
    content = (
      <EmptyPlaceholder
        title="Selected account disconnected"
        subtitle="The selected search account must be connected to find targets"
        imageSrc={ErrorPlaceholderImage}
      />
    );
  } else if (isSearchAccountNavigatorDisconnected) {
    content = (
      <EmptyPlaceholder
        title="Sales Navigator subscription expired"
        subtitle="The selected search account must have an active Sales Navigator subscription to find targets"
        imageSrc={ErrorPlaceholderImage}
      />
    );
  } else if (navigatorLeadsError) {
    content = (
      <ErrorPlaceholder
        errorMessage="Could not display leads data"
        onRetry={refetchNavigatorLeads}
      />
    );
  } else if (isLimitExceeded) {
    content = (
      <EmptyPlaceholder
        imageSrc={ErrorPlaceholderImage}
        title="LinkedIn search limit reached"
        subtitle="Try again later, or select a different search account"
        actionComponent={
          <Button variant="secondary-black" asChild>
            <a
              href={
                getWhitelabelCustomization()
                  ? "https://help.lnconnect.me/en/articles/13619474-linkedin-search-limits"
                  : "https://help.aimfox.com/en/articles/13619474-linkedin-search-limits"
              }
              target="_blank"
              rel="noopener noreferrer"
            >
              Read more
            </a>
          </Button>
        }
      />
    );
  } else if (leads?.length || isFetchingSalesNavigatorLeads) {
    const Component = isDesktop ? NavigatorTable : NavigatorMobileList;

    content = (
      <>
        <Component
          leads={leads}
          isLoading={isFetchingSalesNavigatorLeads}
          pageSize={pageSize}
          totalItemCount={totalCount}
        />

        <PaginationFooter
          pageSize={pageSize}
          setPageSize={setPageSize}
          selectedPage={page}
          setSelectedPage={setPage}
          totalItemCount={totalCount}
          isLastPageHidden
        />
      </>
    );
  } else {
    content = (
      <SearchAndFiltersEmptyPlaceholder
        hasFilters={!!selectedFilters.length}
        hasSearch={!!keywords}
        onClearAll={() => {
          setSelectedFilters([]);
          setKeywords("");
        }}
      />
    );
  }

  const FiltersComponent = isDesktop ? DesktopFilters : MobileFilters;

  const filtersComponent = (
    <FiltersComponent
      accountId={minerAccountId}
      isLoading={isFetchingSalesNavigatorLeads}
      returnedFilters={filters}
      selectedFilters={selectedFilters}
      setSelectedFilters={setSelectedFilters}
    />
  );
  const searchInputComponent = (
    <SearchInput
      onClear={() => {
        setKeywords("");
        setInputValue("");
      }}
      className="lg:w-72"
      value={inputValue}
      onChange={(e) => setInputValue(e.target.value)}
      placeholder="Enter keyword"
      variant={isDesktop ? "sm" : "lg"}
    />
  );

  return (
    <ScrollAndFooter
      leadCount={totalCount}
      isCreatingSource={isCreatingSource}
      onCreate={() =>
        createSource({
          settings: { type: "navigator", filters: selectedFilters, keywords },
          miner: minerAccountId,
        })
      }
    >
      <div className="flex gap-x-4">
        {isDesktop && filtersComponent}
        <div className="flex grow flex-col gap-y-4">
          <div className="flex items-center gap-x-2.5">
            <div className="flex items-center gap-1">
              <span className="lg:hidden text-button-xs text-nowrap ml-1">
                Search by:
              </span>
              <WorkspaceAccountSelector
                selection={minerAccountId}
                setSelection={setMinerAccountId}
                size="sm"
                isNavigatorOnly
                align="start"
              />
            </div>
            {isDesktop && searchInputComponent}
            <div className="flex w-px bg-black-200 h-8 lg:hidden" />
            <UrlTargeting
              createSource={(url) =>
                createSource({
                  settings: { type: "navigator", url },
                  miner: minerAccountId,
                })
              }
              isCreatingSource={isCreatingSource}
            />
          </div>
          <div className="lg:hidden flex items-center gap-x-2">
            {searchInputComponent}
            {filtersComponent}
          </div>
          {content}
        </div>
      </div>
    </ScrollAndFooter>
  );
}
